/* eslint-disable complexity */
import { columnType } from 'components/table/types';
import { activityTypeEnum, tableColumns } from './tables.types';
import _ from 'lodash';
import { customerActivityTypes } from 'constants/customer-account.constants';
import { BalanceStatus, BalanceType, depositType } from 'constants/balance.constants';
import { Box, MenuItem, Stack, TextField, Typography, useTheme } from '@mui/material';
import { SupportRequestStatus, SupportType } from 'constants/requests.constants';
import { apiFunctionsEnum } from 'constants/api.constants';
import { approveFeature } from 'services/pending-requests.service';
import { Flag } from 'theme/elements/icons';
import { generalStatusEnum, generalTranslation } from 'constants/global.constants';
import { setSuccessMessage } from 'state/reducers/ui.reducers';
import { formatNumber } from 'components/FormCreatorComponent/helpers';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { hasPermission } from 'components/AuthorizationComponent';
import { permissionEnums } from 'constants/permission.constants';

export const getSignPerType = (item: Record<string, any>) => {
	switch (item?.type) {
		case activityTypeEnum.PAYMENT:
		case activityTypeEnum.PAYMENT_FEE:
			return '-';
		case activityTypeEnum.BALANCE_ADDITION:
			return item.balanceType === BalanceType.Withdrawal ? '-' : '+';
	}
	return '';
};
export const tableData = (moneyActivities: any) => {
	const activities: any[] = [];
	moneyActivities.forEach((element: any) => {
		const defaultCustomValues = {
			customNickname: element?.nickname,
			feeAndCurrency: element?.fee
				? element?.fee?.currency?.symbol + formatNumber(element?.fee?.amount)
				: '',
			typeHebrew:
				element?.depositType === depositType.Digital && element?.balanceType === BalanceType.Topping
					? generalTranslation['Topping Up the Account Digital']
					: generalTranslation[element?.balanceType || element?.type],
			approverName: element?.boApprovedByUser // Changed from updatedBy
				? `${element?.boApprovedByUser?.firstName} ${element?.boApprovedByUser?.lastName}`
				: null,
			status: element?.status === BalanceStatus.PendingBoSignerReview ? BalanceStatus.Pending : element?.status,
			sumAndCurrency: element?.amount
				? formatNumber(element?.amount) + getSignPerType(element)
				: (element?.sumAndCurrency || '') + getSignPerType(element),
			currencyName: element?.currencyName || element?.currency?.name,
			createdBy:
				element?.type === 'BalanceRefund'
					? element.createdByBoUser
						? `${element.createdByBoUser.firstName} ${element.createdByBoUser.lastName}`
						: 'Created By CC'
					: element.createdBy,
		};

		if (element?.type === activityTypeEnum.PAYMENT_FEE) {
			_.assign(defaultCustomValues, {
				sumAndCurrency: formatNumber(_.get(element, 'fee.amount')) + '-',
			});
		}
		if (element?.type === activityTypeEnum.CONVERSION_FEE) {
			_.assign(defaultCustomValues, {
				sumAndCurrency: formatNumber(_.get(element, 'amount')) + '-',
			});
		}
		// Backend now handles conversion splitting, so just pass through all elements
		activities.push({
			...element,
			...defaultCustomValues,
		});
	});
	return activities;

	/*return data?.map((item: any) => {
    !item?.currency?.name && console.log(item)
    return {
      ...item,
      customNickname: item?.nickname,
      feeAndCurrency: item?.fee ? item?.fee?.currency?.symbol + item?.fee?.amount : '',
      typeHebrew: generalTranslation[item?.balanceType || item?.type],
      updatedBy: item?.boApprovedByUser
        ? `${item?.boApprovedByUser?.firstName} ${item?.boApprovedByUser?.lastName}`
        : null,
      status: item?.status === BalanceStatus.PendingBoSignerReview ? BalanceStatus.Pending : item?.status,
      sumAndCurrency: item?.amount ? item?.amount + getSignPerType(item) : item?.sumAndCurrency + getSignPerType(item),
      currencyName: item?.currencyName || item?.currency?.name
    };
  });*/
};

export const buildColumns = (
	type: customerActivityTypes,
	t: any,
	dispatch?: any,
	tableData?: any,
	setTableData?: any
): columnType[] => {
	const theme = useTheme();
	const editBeneficiaryStatusPermission = hasPermission([permissionEnums.EDIT_BENEFICIARY_STATUS]);
	const sortableColumnsByType: Partial<Record<customerActivityTypes, string[]>> = {
		[customerActivityTypes.MONEY_ACTIVITIES]: ['createdAt', 'typeHebrew', 'statusHebrew'],
		[customerActivityTypes.CONTACTS]: ['createdAt'],
		[customerActivityTypes.BENEFICIARIES]: [
			'createdAt',
			'companyName',
			'customNickname',
			'typeHebrew',
			'statusHebrew',
			'reference',
		],
		[customerActivityTypes.BANK_ACCOUNTS]: [
			'accountOwnerName',
			'bankBranch',
			'accountNumber',
			'statusHebrew',
			'reference',
		],
		[customerActivityTypes.SUPPORT_REQUESTS]: [
			'createdAt',
			'subject',
			'status',
			'updatedAt',
			'boNotes',
			'reference',
		],
	};
	const sortableColumns = sortableColumnsByType[type];
	const columns: columnType[] = [];
	if (_.has(tableColumns, type)) {
		_.keys(tableColumns[type]).map((element: any, index: number) => {
			let customValueHandle = {};
			if (type === customerActivityTypes.SUPPORT_REQUESTS) {
				if (element === 'message') {
					customValueHandle = {
						customValueHandle: (value: any, row: Record<string, any>) => {
							return (
								<Box key={index} sx={{ width: '350px', whiteSpace: 'pre-wrap' }}>
									{value}
								</Box>
							);
						},
					};
				}
				if (element === 'status') {
					customValueHandle = {
						customValueHandle: (value: any, row: Record<string, any>) => {
							return (
								<TextField
									select
									key={index}
									size="small"
									value={value}
									fullWidth
									disabled={[SupportType.Withdrawal, SupportType.WithdrawalWithClose].includes(
										row.type
									)}
									onChange={(e) => {
										const props = {
											name: apiFunctionsEnum.UPDATE_SUPPORT_REQUEST_STATUS as string,
											data: {
												requestId: row.id,
												status: e.target.value,
											},
										};
										dispatch(approveFeature(props))
											.unwrap()
											.then((originalPromiseResult: any) => {
												try {
													let dataObj = _.keyBy(tableData, 'id');
													dataObj = {
														...dataObj,
														[row.id]: originalPromiseResult.supportRequest,
													};
													setTableData(_.values(dataObj));
													dispatch(setSuccessMessage(true));
												} catch (e) {
													console.log(e);
												}
											})
											.catch((rejectedValueOrSerializedError: any) => {
												// handle error here
												console.log(rejectedValueOrSerializedError);
											});
									}}
								>
									{_.values(SupportRequestStatus).map((status, inx) => (
										<MenuItem value={status} key={inx}>
											{t(`label.SupportRequestStatus.${status}`)}
										</MenuItem>
									))}
								</TextField>
							);
						},
					};
				}
			}
			if (type === customerActivityTypes.BENEFICIARIES) {
				if (element === 'companyName') {
					customValueHandle = {
						customValueHandle: (value: any, row: Record<string, any>) => {
							return (
								<Stack direction="row" spacing={1}>
									<Flag
										title={_.lowerCase(String(row?.country?.alpha2Code))}
										styleObj={{ outline: `2px solid ${theme.palette.root.mediumGreen}` }}
									/>
									<Stack>
										<Typography variant="body">{row?.companyName || ''}</Typography>
										<Typography variant="captionRegular" sx={{ color: theme.palette.root.grey }}>
											{row?.currency?.code || ''}
										</Typography>
									</Stack>
								</Stack>
							);
						},
					};
				}
				if (element === 'subStatus') {
					customValueHandle = {
						customValueHandle: (value: any, row: Record<string, any>) => {
							if (row.status === generalStatusEnum.DECLINED_FOR_REPAIR) {
								return t(
									'page.customerAccounts.forms.beneficiaries.deniedMessages.subStatus.DeclinedForRepair'
								);
							} else if (row.status === generalStatusEnum.DECLINED) {
								return getDeclinedReasonText(row);
							} else {
								return '';
							}
						},
					};
				}
				if (element === 'lastUpdatedByBoUser') {
					customValueHandle = {
						customValueHandle: (value: any, row: Record<string, any>) => {
							return row?.statusChangeByBoUser?.displayName || '';
						},
					};
				}
				if (element === 'edit') {
					customValueHandle = {
						customValueHandle: (value: any, row: Record<string, any>) => {
							return <img key="editIcon" src={'../icons/pencil.svg'} style={{ cursor: 'pointer' }} />;
						},
					};
				}
			}
			if (type === customerActivityTypes.MONEY_ACTIVITIES && element === 'typeHebrew') {
				customValueHandle = {
					customValueHandle: (value: any, row: Record<string, any>) => {
						return (
							<Stack direction="row">
								{row.type === activityTypeEnum.CONVERSION_FEE && row.benefitIncluded ? (
									<img src={'/icons/gold_star.svg'} style={{ marginLeft: '2px' }} />
								) : (
									''
								)}
								{value}
							</Stack>
						);
					},
				};
			}
			const props: columnType = {
				fieldId: 'id',
				name: element,
				title: t('page.customerAccounts.tables.' + type + '.field.' + element),
				type: tableColumns[type][element],
				sortable: sortableColumns ? sortableColumns.includes(element) : element !== 'edit',
				...customValueHandle,
			};
			columns.push(props);
			if (
				element === 'edit' &&
				!editBeneficiaryStatusPermission &&
				type === customerActivityTypes.BENEFICIARIES
			) {
				columns.pop();
			}
		});
	}
	return columns;
};

export const prepareTableDataForFileDownload = (filteredDataForExcel: any[], text: string, companyData: any) => {
	try {
		let filteredData = filteredDataForExcel.length ? _.orderBy(filteredDataForExcel, ['createdAt'], ['desc']) : [];

		filteredData = filteredData.map((item: any) => {
			if (item.type === activityTypeEnum.CONVERSION) {
				return {
					created_at: format(new Date(item.createdAt), 'dd.MM.yyyy HH:mm'),
					created_by: item?.createdBy,
					approved_by: item?.approverName,
					type: item?.typeHebrew,
					amount: item?.sumAndCurrency ? item?.sumAndCurrency.replace(/^([\d,.]+)([+-])$/, '$2$1') : '0',
					currency:
						item?.currencyType == 'sell'
							? item?.pendingQuote?.clientSellCurrency
							: item?.pendingQuote?.clientBuyCurrency,
					accumulatedBalance:
						item?.currencyType == 'sell'
							? item?.accumulatedBalanceSellCurrency
							: item?.accumulatedBalanceBuyCurrency,
					status: item?.statusHebrew || item?.status,
					reference: item?.reference,
				};
			} else {
				return {
					created_at: format(new Date(item.createdAt), 'dd.MM.yyyy HH:mm'),
					created_by: item?.createdBy,
					approved_by: item?.approverName,
					type: item?.typeHebrew,
					amount: item?.amount
						? item?.balanceType === BalanceType?.Withdrawal
							? `-${item?.amount}`
							: `+${item?.amount}`
						: '0',
					currency: item?.currency?.code,
					accumulatedBalance: item?.accumulatedBalance || '',
					status: item?.statusHebrew || item?.status,
					reference: item?.reference,
				};
			}
		});

		const ws = filteredData.length
			? XLSX.utils.json_to_sheet(filteredData)
			: XLSX.utils.aoa_to_sheet([['No data']]);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws);
		const fileSuffix = '.xlsx';
		XLSX.writeFile(
			wb,
			`${text}-${companyData?.basicData?.englishName || companyData?.basicData?.hebrewName}${fileSuffix}`
		);
	} catch (error) {
		console.log('error', error);
	}
};

export const getDeclinedReasonText = (data: any) => {
	if (_.has(data, 'beneficiaryDeniedMessages')) {
		const messages = data?.beneficiaryDeniedMessages;
		const message = messages[messages.length - 1];

		return _.has(message, 'translation[0]') ? message.translation[0]?.he : message?.message;
	}
};
